import {AnchoredOverlay, IconButton, RelativeTime, Stack, Text, Timeline, Tooltip} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {CommentIcon, FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import {useId, useRef, useState, type ReactNode} from 'react'
import type {TimelineItem} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {TimelineEvent, TimelineItemText, timelineItemTextHasInteractiveContent} from './TimelineEvent'
import {TimelineAuthor} from './TimelineAuthor'
import {TimelineItemBadge} from './TimelineItemBadge'
import {OverlayPortalContainer} from '../OverlayPortalContainer'
import styles from './TimelinePanel.module.css'
import eventStyles from './TimelineEvent.module.css'

const MINIMUM_AUTHOR_GROUP_SIZE = 3
const MAXIMUM_ACTIVITY_GROUP_INTERVAL_MS = 60_000
const activityTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

type TimelineUser = NonNullable<TimelineItem['user']>
type TimelineListEntry =
  | {type: 'event'; item: TimelineItem}
  | {type: 'activityGroup'; user: TimelineUser; items: TimelineItem[]}

function activitiesAreWithinGroupingWindow(firstItem: TimelineItem, currentItem: TimelineItem) {
  if (!firstItem.timestamp || !currentItem.timestamp) return false

  const firstTimestamp = Date.parse(firstItem.timestamp)
  const currentTimestamp = Date.parse(currentItem.timestamp)
  if (!Number.isFinite(firstTimestamp) || !Number.isFinite(currentTimestamp)) return false

  return Math.abs(currentTimestamp - firstTimestamp) <= MAXIMUM_ACTIVITY_GROUP_INTERVAL_MS
}

function groupTimelineItems(items: TimelineItem[]): TimelineListEntry[] {
  const entries: TimelineListEntry[] = []
  let consecutiveItems: TimelineItem[] = []
  let consecutiveUser: TimelineUser | undefined

  const flushConsecutiveItems = () => {
    if (consecutiveUser && consecutiveItems.length >= MINIMUM_AUTHOR_GROUP_SIZE) {
      entries.push({type: 'activityGroup', user: consecutiveUser, items: consecutiveItems})
    } else {
      for (const item of consecutiveItems) {
        entries.push({type: 'event', item})
      }
    }

    consecutiveItems = []
    consecutiveUser = undefined
  }

  for (const item of items) {
    const user = item.user

    if (!user?.displayLogin || !item.timestamp || timelineItemTextHasInteractiveContent(item)) {
      flushConsecutiveItems()
      entries.push({type: 'event', item})
      continue
    }

    const firstItem = consecutiveItems[0]
    if (
      consecutiveUser?.id !== user.id ||
      (firstItem !== undefined && !activitiesAreWithinGroupingWindow(firstItem, item))
    ) {
      flushConsecutiveItems()
      consecutiveUser = user
    }

    consecutiveItems.push(item)
  }

  flushConsecutiveItems()
  return entries
}

function activitySeparator(index: number, count: number) {
  if (index === 0) return ''
  if (index === count - 1) return count === 2 ? ' and ' : ', and '
  return ', '
}

function ActivityTimeAction({item, portalName}: {item: TimelineItem; portalName: string}) {
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef<HTMLElement>(null)
  const timeRef = useRef<HTMLSpanElement>(null)
  const {isServer} = useRuntimeEnvironment()
  const activityTime =
    !isServer && item.timestamp
      ? activityTimeFormatter.format(new Date(item.timestamp))
      : item.timestamp || 'Unknown time'

  return (
    <AnchoredOverlay
      anchorRef={anchorRef}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      side="outside-bottom"
      align="center"
      width="auto"
      overlayProps={{
        role: 'dialog',
        'aria-label': 'Activity time',
        initialFocusRef: timeRef,
        portalContainerName: portalName,
      }}
      renderAnchor={props => (
        <Tooltip ref={anchorRef} text={activityTime} type="description" direction="n" _privateDisableTooltip={isOpen}>
          {/* Native buttons cannot wrap inline with the sentence. Primer supplies the keyboard handlers. */}
          <span {...props} role="button" aria-haspopup="dialog" className={styles.activityTimeAction}>
            <TimelineItemText item={item} />
          </span>
        </Tooltip>
      )}
    >
      <Stack padding="condensed">
        <Text ref={timeRef} tabIndex={-1} size="small">
          {activityTime}
        </Text>
      </Stack>
    </AnchoredOverlay>
  )
}

function ActivityTimestamp({items}: {items: TimelineItem[]}) {
  const finalItem = items.at(-1)
  if (!finalItem) return null

  return (
    <span className={`${eventStyles.timestamp} flex-shrink-0 fgColor-muted`}>
      <RelativeTime datetime={finalItem.timestamp} format="micro" noTitle />
    </span>
  )
}

function ActivityGroup({user, items}: {user: TimelineUser; items: TimelineItem[]}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const portalId = `activity-times-${useId()}`

  return (
    <Timeline.Item className={`${styles.activityItem} ${styles.activityGroupItem}`}>
      <Timeline.Badge className={styles.activityBadge}>
        <IconButton
          icon={isExpanded ? FoldIcon : UnfoldIcon}
          variant="invisible"
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${items.length} activities`}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(expanded => !expanded)}
        />
      </Timeline.Badge>
      <Timeline.Body className={styles.activityBody}>
        <div className="d-flex flex-row flex-justify-between flex-items-start">
          <span className="flex-1 min-width-0 mr-2">
            <TimelineAuthor login={user.displayLogin} avatarUrl={user.avatarUrl} />{' '}
            {!isExpanded &&
              items.map((item, index) => (
                <span key={item.id}>
                  {activitySeparator(index, items.length)}
                  <ActivityTimeAction item={item} portalName={portalId} />
                </span>
              ))}
          </span>
          {!isExpanded && <ActivityTimestamp items={items} />}
        </div>
        {isExpanded && (
          <Timeline className="mt-2">
            {items.map(item => (
              <Timeline.Item key={item.id} className={styles.activityItem}>
                <TimelineItemBadge item={item} />
                <Timeline.Body
                  className={`${styles.activityBody} d-flex flex-row flex-justify-between flex-items-start`}
                >
                  <span className="flex-1 min-width-0 mr-2">
                    <TimelineItemText item={item} />
                  </span>
                  <ActivityTimestamp items={[item]} />
                </Timeline.Body>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
        <OverlayPortalContainer portalName={portalId} />
      </Timeline.Body>
    </Timeline.Item>
  )
}

export function TimelineItemList({
  items,
  clipSidebar,
  sentinel,
}: {
  items: TimelineItem[]
  clipSidebar?: boolean | 'start' | 'end'
  sentinel?: ReactNode
}) {
  if (items.length === 0 && !sentinel) {
    return (
      <div className={styles.emptyState}>
        <Blankslate>
          <Blankslate.Visual>
            <CommentIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading as="h4">No activity</Blankslate.Heading>
          <Blankslate.Description>Activity on this pull request will appear here.</Blankslate.Description>
        </Blankslate>
      </div>
    )
  }

  const entries = groupTimelineItems(items)

  return (
    <Timeline clipSidebar={clipSidebar}>
      {entries.map(entry =>
        entry.type === 'activityGroup' ? (
          <ActivityGroup key={`activity-group-${entry.items[0]?.id}`} user={entry.user} items={entry.items} />
        ) : (
          <TimelineEvent key={entry.item.id} item={entry.item} />
        ),
      )}
      {sentinel}
    </Timeline>
  )
}
